/**
 * Flight pattern and DSP calculation functions.
 * All functions are pure — they take inputs and return results.
 * The component is responsible for setting state from the results.
 */

import { LAT_TO_METERS, FULL_FRAME_DIAGONAL_MM, lngToMetersAtLat } from './coordinateUtils';
import { isPointInPolygon, clipLineToPolygon, calculatePolygonArea } from './geometry';
import { DRONE_MODELS, DSP_LENSES } from '../constants/flightConstants';

/**
 * Calculate DSP flight altitude based on lens and plot area.
 * @param {number} plotArea - Plot area in m²
 * @param {string} lensId - Lens identifier key
 * @returns {number} Altitude in meters
 */
export const calculateDSPAltitude = (plotArea, lensId) => {
	const lens = DSP_LENSES[lensId];
	if (!lens) return 0;
	const groundWidth = Math.sqrt(plotArea / 0.75);
	return (lens.focalLength * groundWidth) / lens.sensorWidth;
};

/**
 * Calculate plot area from flight altitude (inverse of calculateDSPAltitude).
 * @param {number} altitude - Flight altitude in meters
 * @param {string} lensId - Lens identifier key
 * @returns {number} Plot area in m² (rounded)
 */
export const calculatePlotAreaFromAltitude = (altitude, lensId) => {
	const lens = DSP_LENSES[lensId];
	if (!lens || altitude <= 0) return 0;
	const groundWidth = (lens.sensorWidth * altitude) / lens.focalLength;
	return Math.round(groundWidth * groundWidth * 0.75);
};

/**
 * Calculate DSP footprints and flight lines from waypoints.
 * @param {Object} params
 * @param {{ latitude: number, longitude: number }[]} params.waypoints
 * @param {number} params.plotArea - Plot area in m²
 * @param {string} params.lensId - Lens identifier
 * @param {number} params.alignmentAngle - Grid alignment in degrees
 * @returns {{ footprints: Array, lines: Array }}
 */
export const computeDSPPattern = ({ waypoints, plotArea, lensId, alignmentAngle }) => {
	if (waypoints.length === 0) {
		return { footprints: [], lines: [] };
	}

	const altitude = calculateDSPAltitude(plotArea, lensId);
	const lens = DSP_LENSES[lensId];

	if (!lens || altitude <= 0) {
		return { footprints: [], lines: [] };
	}

	const groundWidth = (lens.sensorWidth * altitude) / lens.focalLength;
	const groundHeight = groundWidth * 0.75;

	const centerLat = waypoints[0].latitude;
	const lngToMeters = lngToMetersAtLat(centerLat);

	const latOffset = groundHeight / 2 / LAT_TO_METERS;
	const lngOffset = groundWidth / 2 / lngToMeters;

	const angleRad = (alignmentAngle * Math.PI) / 180;
	const cosAngle = Math.cos(angleRad);
	const sinAngle = Math.sin(angleRad);

	const rotatePoint = (lat, lng, cLat, cLng) => {
		const dx = (lng - cLng) * lngToMeters;
		const dy = (lat - cLat) * LAT_TO_METERS;
		return {
			lat: cLat + (dx * sinAngle + dy * cosAngle) / LAT_TO_METERS,
			lng: cLng + (dx * cosAngle - dy * sinAngle) / lngToMeters,
		};
	};

	const footprints = waypoints.map((wp, index) => {
		const corners = [
			{ lat: wp.latitude - latOffset, lng: wp.longitude - lngOffset },
			{ lat: wp.latitude - latOffset, lng: wp.longitude + lngOffset },
			{ lat: wp.latitude + latOffset, lng: wp.longitude + lngOffset },
			{ lat: wp.latitude + latOffset, lng: wp.longitude - lngOffset },
		];
		const rotatedCorners = corners.map(c =>
			rotatePoint(c.lat, c.lng, wp.latitude, wp.longitude)
		);
		return {
			id: index,
			center: { lat: wp.latitude, lng: wp.longitude },
			corners: rotatedCorners,
			altitude,
			groundWidth,
			groundHeight,
		};
	});

	const lines =
		waypoints.length > 1
			? waypoints.slice(0, -1).map((wp, i) => [
					{ lat: wp.latitude, lng: wp.longitude },
					{ lat: waypoints[i + 1].latitude, lng: waypoints[i + 1].longitude },
				])
			: [];

	return { footprints, lines };
};

/**
 * Sort waypoints in boustrophedon (serpentine) pattern for efficient flight path.
 * Groups by approximate row, then alternates direction per row.
 */
const sortBoustrophedon = (waypoints, spacing, centerLat, centerLng, sinAngle, cosAngle) => {
	if (waypoints.length <= 1) return waypoints;

	const lngToMeters = lngToMetersAtLat(centerLat);
	const rowTolerance = spacing * 0.3;

	const withRotated = waypoints.map(wp => {
		const x = (wp.longitude - centerLng) * lngToMeters;
		const y = (wp.latitude - centerLat) * LAT_TO_METERS;
		return {
			...wp,
			rotY: -x * sinAngle + y * cosAngle,
			rotX: x * cosAngle + y * sinAngle,
		};
	});
	withRotated.sort((a, b) => a.rotY - b.rotY);

	const rows = [];
	let currentRow = [withRotated[0]];
	for (let i = 1; i < withRotated.length; i++) {
		if (Math.abs(withRotated[i].rotY - currentRow[0].rotY) < rowTolerance) {
			currentRow.push(withRotated[i]);
		} else {
			rows.push(currentRow);
			currentRow = [withRotated[i]];
		}
	}
	rows.push(currentRow);

	const sorted = [];
	rows.forEach((row, idx) => {
		row.sort((a, b) => a.rotX - b.rotX);
		if (idx % 2 === 1) row.reverse();
		sorted.push(...row.map(({ latitude, longitude }) => ({ latitude, longitude })));
	});

	return sorted;
};

/**
 * Generate DSP waypoints within a polygon using grid-based or random distribution.
 * @param {Object} params
 * @param {{ lat: number, lng: number }[]} params.polygonPoints
 * @param {Object} params.dspSettings - { pointCount, distributionMethod, minimumDistance, maximumDistance, alignmentAngle }
 * @param {boolean} params.showDistanceConstraints
 * @returns {{ latitude: number, longitude: number }[]} Generated waypoints
 */
export const computeDSPWaypoints = ({ polygonPoints, dspSettings, showDistanceConstraints }) => {
	if (polygonPoints.length < 3) return [];

	const { pointCount, distributionMethod, minimumDistance, maximumDistance, alignmentAngle } =
		dspSettings;

	const lats = polygonPoints.map(p => p.lat);
	const lngs = polygonPoints.map(p => p.lng);
	const minLat = Math.min(...lats);
	const maxLat = Math.max(...lats);
	const minLng = Math.min(...lngs);
	const maxLng = Math.max(...lngs);

	const centerLat = (minLat + maxLat) / 2;
	const centerLng = (minLng + maxLng) / 2;
	const lngToMeters = lngToMetersAtLat(centerLat);

	const polygonMeters = polygonPoints.map(p => ({
		x: (p.lng - centerLng) * lngToMeters,
		y: (p.lat - centerLat) * LAT_TO_METERS,
	}));

	let polygonArea = 0;
	for (let i = 0; i < polygonMeters.length; i++) {
		const j = (i + 1) % polygonMeters.length;
		polygonArea += polygonMeters[i].x * polygonMeters[j].y;
		polygonArea -= polygonMeters[j].x * polygonMeters[i].y;
	}
	polygonArea = Math.abs(polygonArea / 2);

	const targetSpacing = Math.sqrt(polygonArea / pointCount);
	const spacing = showDistanceConstraints
		? Math.min(Math.max(targetSpacing, minimumDistance), maximumDistance)
		: targetSpacing;

	const angleRad = (alignmentAngle * Math.PI) / 180;
	const cosAngle = Math.cos(angleRad);
	const sinAngle = Math.sin(angleRad);

	const rotatedPoints = polygonMeters.map(p => ({
		x: p.x * cosAngle + p.y * sinAngle,
		y: -p.x * sinAngle + p.y * cosAngle,
	}));

	const rotatedMinX = Math.min(...rotatedPoints.map(p => p.x));
	const rotatedMaxX = Math.max(...rotatedPoints.map(p => p.x));
	const rotatedMinY = Math.min(...rotatedPoints.map(p => p.y));
	const rotatedMaxY = Math.max(...rotatedPoints.map(p => p.y));

	const newWaypoints = [];

	if (distributionMethod === 'even') {
		const numCols = Math.max(1, Math.ceil((rotatedMaxX - rotatedMinX) / spacing));
		const numRows = Math.max(1, Math.ceil((rotatedMaxY - rotatedMinY) / spacing));

		for (let row = 0; row <= numRows; row++) {
			for (let col = 0; col <= numCols; col++) {
				const rotX = rotatedMinX + col * spacing;
				const rotY = rotatedMinY + row * spacing;
				const x = rotX * cosAngle - rotY * sinAngle;
				const y = rotX * sinAngle + rotY * cosAngle;
				const lat = centerLat + y / LAT_TO_METERS;
				const lng = centerLng + x / lngToMeters;

				if (isPointInPolygon({ lat, lng }, polygonPoints)) {
					newWaypoints.push({ latitude: lat, longitude: lng });
				}
			}
		}
	} else if (distributionMethod === 'random') {
		const maxAttempts = pointCount * 200;
		let attempts = 0;

		while (newWaypoints.length < pointCount && attempts < maxAttempts) {
			attempts++;
			const rotX = rotatedMinX + Math.random() * (rotatedMaxX - rotatedMinX);
			const rotY = rotatedMinY + Math.random() * (rotatedMaxY - rotatedMinY);
			const x = rotX * cosAngle - rotY * sinAngle;
			const y = rotX * sinAngle + rotY * cosAngle;
			const lat = centerLat + y / LAT_TO_METERS;
			const lng = centerLng + x / lngToMeters;

			if (!isPointInPolygon({ lat, lng }, polygonPoints)) continue;

			let tooClose = false;
			for (const wp of newWaypoints) {
				const dx = (lng - wp.longitude) * lngToMeters;
				const dy = (lat - wp.latitude) * LAT_TO_METERS;
				if (Math.sqrt(dx * dx + dy * dy) < minimumDistance) {
					tooClose = true;
					break;
				}
			}
			if (!tooClose) newWaypoints.push({ latitude: lat, longitude: lng });
		}
	}

	return sortBoustrophedon(newWaypoints, spacing, centerLat, centerLng, sinAngle, cosAngle);
};

/**
 * Generate waypoints for full area coverage with specified overlap.
 * @param {Object} params
 * @param {{ lat: number, lng: number }[]} params.polygonPoints
 * @param {number} params.plotArea - Plot area in m²
 * @param {number} params.maxOverlap - Overlap percentage
 * @param {number} params.alignmentAngle - Grid alignment in degrees
 * @returns {{ waypoints: { latitude: number, longitude: number }[], pointCount: number }}
 */
export const computeAutoCoverage = ({ polygonPoints, plotArea, maxOverlap, alignmentAngle }) => {
	if (polygonPoints.length < 3) return { waypoints: [], pointCount: 0 };

	const groundWidth = Math.sqrt(plotArea / 0.75);
	const groundHeight = groundWidth * 0.75;
	const overlapFraction = maxOverlap / 100;
	const spacingX = groundWidth * (1 - overlapFraction);
	const spacingY = groundHeight * (1 - overlapFraction);

	const lats = polygonPoints.map(p => p.lat);
	const lngs = polygonPoints.map(p => p.lng);
	const minLat = Math.min(...lats);
	const maxLat = Math.max(...lats);
	const minLng = Math.min(...lngs);
	const maxLng = Math.max(...lngs);

	const centerLat = (minLat + maxLat) / 2;
	const centerLng = (minLng + maxLng) / 2;
	const lngToMeters = lngToMetersAtLat(centerLat);

	const polygonMeters = polygonPoints.map(p => ({
		x: (p.lng - centerLng) * lngToMeters,
		y: (p.lat - centerLat) * LAT_TO_METERS,
	}));

	const angleRad = (alignmentAngle * Math.PI) / 180;
	const cosAngle = Math.cos(angleRad);
	const sinAngle = Math.sin(angleRad);

	const rotatedPoints = polygonMeters.map(p => ({
		x: p.x * cosAngle + p.y * sinAngle,
		y: -p.x * sinAngle + p.y * cosAngle,
	}));

	const rotatedMinX = Math.min(...rotatedPoints.map(p => p.x));
	const rotatedMaxX = Math.max(...rotatedPoints.map(p => p.x));
	const rotatedMinY = Math.min(...rotatedPoints.map(p => p.y));
	const rotatedMaxY = Math.max(...rotatedPoints.map(p => p.y));

	const numCols = Math.max(1, Math.ceil((rotatedMaxX - rotatedMinX) / spacingX));
	const numRows = Math.max(1, Math.ceil((rotatedMaxY - rotatedMinY) / spacingY));

	const newWaypoints = [];
	for (let row = 0; row <= numRows; row++) {
		for (let col = 0; col <= numCols; col++) {
			const rotX = rotatedMinX + col * spacingX;
			const rotY = rotatedMinY + row * spacingY;
			const x = rotX * cosAngle - rotY * sinAngle;
			const y = rotX * sinAngle + rotY * cosAngle;
			const lat = centerLat + y / LAT_TO_METERS;
			const lng = centerLng + x / lngToMeters;

			if (isPointInPolygon({ lat, lng }, polygonPoints)) {
				newWaypoints.push({ latitude: lat, longitude: lng });
			}
		}
	}

	const sorted = sortBoustrophedon(
		newWaypoints,
		spacingY,
		centerLat,
		centerLng,
		sinAngle,
		cosAngle
	);
	return { waypoints: sorted, pointCount: sorted.length };
};

/**
 * Calculate orthomosaic flight pattern and statistics.
 * @param {Object} params
 * @param {{ lat: number, lng: number }[]} params.polygonPoints
 * @param {string} params.droneModelId
 * @param {number} params.flightHeight
 * @param {number} params.sideOverlap - Percentage (0-100)
 * @param {number} params.frontOverlap - Percentage (0-100)
 * @param {number} params.speed - m/s
 * @param {number} params.flightAngle - degrees
 * @returns {{ lines: Array, stats: Object } | null}
 */
export const computeFlightPattern = ({
	polygonPoints,
	droneModelId,
	flightHeight,
	sideOverlap,
	frontOverlap,
	speed,
	flightAngle,
}) => {
	if (polygonPoints.length < 3 || !droneModelId) return null;

	const drone = DRONE_MODELS.find(d => d.id === droneModelId);
	if (!drone) return null;

	const { camera } = drone;
	const height = flightHeight;
	const sideOvl = sideOverlap / 100;
	const frontOvl = frontOverlap / 100;

	const cropFactor =
		FULL_FRAME_DIAGONAL_MM / Math.sqrt(camera.sensorWidth ** 2 + camera.sensorHeight ** 2);
	const actualFocalLength = camera.focalLength / cropFactor;

	const groundWidthPerImage = (camera.sensorWidth * height) / actualFocalLength;
	const groundHeightPerImage = (camera.sensorHeight * height) / actualFocalLength;
	const gsd = (groundWidthPerImage * 100) / camera.imageWidth;
	const lineSpacing = groundWidthPerImage * (1 - sideOvl);
	const photoDistance = groundHeightPerImage * (1 - frontOvl);
	const photoInterval = Math.max(photoDistance / speed, camera.minInterval);
	// NOTE: if photoDistance/speed < minInterval, actual front overlap will be
	// lower than requested. No user-facing warning is currently emitted.

	const lats = polygonPoints.map(p => p.lat);
	const lngs = polygonPoints.map(p => p.lng);
	const minLat = Math.min(...lats);
	const maxLat = Math.max(...lats);
	const minLng = Math.min(...lngs);
	const maxLng = Math.max(...lngs);

	const lngToMeters = lngToMetersAtLat((minLat + maxLat) / 2);
	const centerLat = (minLat + maxLat) / 2;
	const centerLng = (minLng + maxLng) / 2;

	const angleRad = (flightAngle * Math.PI) / 180;
	const cosAngle = Math.cos(angleRad);
	const sinAngle = Math.sin(angleRad);

	const polygonMeters = polygonPoints.map(p => ({
		x: (p.lng - centerLng) * lngToMeters,
		y: (p.lat - centerLat) * LAT_TO_METERS,
	}));

	const rotatedPoints = polygonMeters.map(p => ({
		x: p.x * cosAngle + p.y * sinAngle,
		y: -p.x * sinAngle + p.y * cosAngle,
	}));

	const rotatedMinX = Math.min(...rotatedPoints.map(p => p.x));
	const rotatedMaxX = Math.max(...rotatedPoints.map(p => p.x));
	const rotatedMinY = Math.min(...rotatedPoints.map(p => p.y));
	const rotatedMaxY = Math.max(...rotatedPoints.map(p => p.y));

	const rotatedWidth = rotatedMaxX - rotatedMinX;
	const actualNumLines = Math.ceil(rotatedWidth / lineSpacing) + 1;

	const lines = [];
	let totalFlightDistance = 0;
	let totalPhotos = 0;

	for (let i = 0; i < actualNumLines; i++) {
		const rotatedX = rotatedMinX + lineSpacing * i;
		const lineStartRotated = { x: rotatedX, y: rotatedMinY - 100 };
		const lineEndRotated = { x: rotatedX, y: rotatedMaxY + 100 };

		const lineStartMeters = {
			x: lineStartRotated.x * cosAngle - lineStartRotated.y * sinAngle,
			y: lineStartRotated.x * sinAngle + lineStartRotated.y * cosAngle,
		};
		const lineEndMeters = {
			x: lineEndRotated.x * cosAngle - lineEndRotated.y * sinAngle,
			y: lineEndRotated.x * sinAngle + lineEndRotated.y * cosAngle,
		};

		const lineStart = {
			lat: centerLat + lineStartMeters.y / LAT_TO_METERS,
			lng: centerLng + lineStartMeters.x / lngToMeters,
		};
		const lineEnd = {
			lat: centerLat + lineEndMeters.y / LAT_TO_METERS,
			lng: centerLng + lineEndMeters.x / lngToMeters,
		};

		const clippedSegments = clipLineToPolygon(lineStart, lineEnd, polygonPoints);

		clippedSegments.forEach((segment, segIdx) => {
			if ((i + segIdx) % 2 === 1) segment.reverse();
			lines.push(segment);

			const segLengthMeters = Math.sqrt(
				Math.pow((segment[1].lat - segment[0].lat) * LAT_TO_METERS, 2) +
					Math.pow((segment[1].lng - segment[0].lng) * lngToMeters, 2)
			);
			totalFlightDistance += segLengthMeters;
			totalPhotos += Math.ceil(segLengthMeters / photoDistance);
		});
	}

	// TODO(verify-with-PO): 1.5× multiplier is a heuristic for turn distance.
	// Does not model acceleration/deceleration. Validate against flight logs.
	const turnDistance = (lines.length - 1) * lineSpacing * 1.5;
	totalFlightDistance += turnDistance;
	const flightTimeMinutes = totalFlightDistance / speed / 60;

	return {
		lines,
		stats: {
			gsd: gsd.toFixed(2),
			groundCoverage: {
				width: groundWidthPerImage.toFixed(1),
				height: groundHeightPerImage.toFixed(1),
			},
			lineSpacing: lineSpacing.toFixed(1),
			photoDistance: photoDistance.toFixed(1),
			photoInterval: photoInterval.toFixed(1),
			numLines: lines.length,
			totalDistance: (totalFlightDistance / 1000).toFixed(2),
			flightTime: flightTimeMinutes.toFixed(1),
			totalPhotos,
			area: calculatePolygonArea(polygonPoints),
		},
	};
};
